import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Package, ShoppingBag, X, Home, FileText, 
  Star, Settings, BookOpen, Layout, Megaphone, Palette,
  Menu as MenuIcon, Image, Grid, Layers
} from 'lucide-react';
import { Button } from './ui/button';

const AdminSidebar = ({ active, open, onClose }) => {
  const navigate = useNavigate();

  const menuGroups = [
    {
      title: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart, path: '/admin/dashboard' },
      ]
    },
    {
      title: 'Content Management',
      items: [
        { id: 'homepage', label: 'Homepage', icon: Home, path: '/admin/homepage' },
        { id: 'categories', label: 'Categories', icon: Grid, path: '/admin/categories' },
        { id: 'products', label: 'Products', icon: Package, path: '/admin/products' },
        { id: 'testimonials', label: 'Testimonials', icon: Star, path: '/admin/testimonials' },
        { id: 'blog', label: 'Blog Posts', icon: BookOpen, path: '/admin/blog' },
      ]
    },
    {
      title: 'Site Settings',
      items: [
        { id: 'navigation', label: 'Navigation', icon: MenuIcon, path: '/admin/navigation' },
        { id: 'announcement', label: 'Announcement Bar', icon: Megaphone, path: '/admin/announcement' },
        { id: 'footer', label: 'Footer', icon: Layers, path: '/admin/footer' },
        { id: 'theme', label: 'Theme & Colors', icon: Palette, path: '/admin/theme' },
        { id: 'settings', label: 'Site Settings', icon: Settings, path: '/admin/settings' },
      ]
    },
    {
      title: 'Pages',
      items: [
        { id: 'pages', label: 'About/Contact/FAQ', icon: Layout, path: '/admin/pages' },
        { id: 'content', label: 'Legal Pages', icon: FileText, path: '/admin/content' },
      ]
    },
    {
      title: 'Orders',
      items: [
        { id: 'orders', label: 'All Orders', icon: ShoppingBag, path: '/admin/orders' },
      ]
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <div
        className={`fixed top-0 left-0 bottom-0 w-64 bg-[#1A0505] z-50 transition-transform duration-300 lg:translate-x-0 overflow-y-auto ${
          open ? 'translate-x-0' : '-translate-x-full'
        } lg:block`}
        data-testid="admin-sidebar"
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-gold font-accent text-lg">ॐ</span>
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-gold">Indicraft</h2>
              <span className="text-[10px] text-gold/60">ADMIN PANEL</span>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="lg:hidden p-2 text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <nav className="p-3">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.title} className={groupIndex > 0 ? 'mt-6' : ''}>
              <p className="px-4 text-xs font-medium text-gold/50 uppercase tracking-wider mb-2">
                {group.title}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    data-testid={`nav-${item.id}`}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg mb-1 transition-all ${
                      isActive
                        ? 'bg-gold text-maroon-dark font-semibold'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-4 mt-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-white/20 text-white/70 hover:text-white hover:bg-white/10"
          >
            Logout
          </Button>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
